import React from 'react';
import T from 'prop-types';
import { map, omit } from 'lodash-es';

import SimpleDropdown from '../../../../../components/Dropdowns/SimpleDropdown';
import { V2_COHORTS } from '../../../../../utils/advanced-metrics';
import {
  activityMetrics,
  advancedMetrics,
  engagementMetrics,
  retentionMetrics,
} from '../../../config/drivers-metrics';

const MetricList = ({
  selected,
  options,
  disabled,
  menuWidth = 240,
  appendButtonText,
  onChange,
}) => {
  const decoratedOptions = appendButtonText ? options.map(option => ({
    ...option,
    buttonLabel: (
      <>
        <span>{option.label}</span>
        <span className="label-caption">{appendButtonText}</span>
      </>
    ),
  })) : options;

  return (
    <div className="metric-list-dropdown">
      <SimpleDropdown
        selected={selected}
        options={decoratedOptions}
        caret
        frameless
        disabled={disabled}
        menuWidth={menuWidth}
        isActiveSelectable={false}
        onChange={onChange}
      />
    </div>
  );
};

const basePropTypes = {
  selected: T.string.isRequired,
  disabled: T.bool,
  onChange: T.func.isRequired,
};

MetricList.propTypes = {
  ...basePropTypes,
  appendButtonText: T.string,
  options: T.arrayOf(T.shape({
    label: T.string,
    value: T.string,
    disabled: T.bool,
  })).isRequired,
};

// ActivityMetricDropdown
const activityOptions = map(activityMetrics, (label, value) => ({
  label,
  value,
  className: 'menu-item--14',
}));

export const ActivityMetricDropdown = ({
  selected,
  disabled,
  appendButtonText,
  onChange,
}) => (
  <MetricList
    selected={selected}
    disabled={disabled}
    options={activityOptions}
    appendButtonText={appendButtonText}
    onChange={onChange}
  />
);

ActivityMetricDropdown.propTypes = {
  ...basePropTypes,
  appendButtonText: T.string,
};

// RetentionMetricDropdown/EngagementMetricDropdown
const retentionOptions = map(retentionMetrics, (label, value) => ({
  label,
  value,
  className: 'menu-item--14',
}));

const engagementOptions = map(engagementMetrics, (label, value) => ({
  label,
  value,
  className: 'menu-item--14',
}));

export const AdvancedActivityMetricDropdown = ({
  selected,
  disabled,
  sectionKey,
  appendButtonText,
  onChange,
}) => (
  <MetricList
    selected={selected}
    disabled={disabled}
    options={sectionKey === 'retentionChart' ? retentionOptions : engagementOptions}
    appendButtonText={appendButtonText}
    onChange={onChange}
  />
);

AdvancedActivityMetricDropdown.propTypes = {
  ...basePropTypes,
  sectionKey: T.oneOf(['retentionChart', 'engagementChart']).isRequired,
  appendButtonText: T.string,
};

// AdvancedMetricDropdown
const advancedOptions = map(advancedMetrics, (label, value) => ({
  label,
  value,
  className: 'menu-item--14',
}));

export const AdvancedMetricDropdown = ({
  selected,
  disabled,
  onChange,
}) => (
  <MetricList
    selected={selected}
    disabled={disabled}
    options={advancedOptions}
    menuWidth={252}
    onChange={onChange}
  />
);

AdvancedMetricDropdown.propTypes = basePropTypes;

// SegmentsMetricDropdown
const segmentsOptions = map(omit(V2_COHORTS, ['younger_users']), (label, value) => ({
  label,
  value,
  className: 'menu-item--14',
  buttonLabel: `Segment: ${label}`,
}));

export const getFirstAvailableSegment = (disabledSegment) => {
  const availableOption = segmentsOptions.find(option => option.value !== disabledSegment);
  return availableOption ? availableOption.value : null;
};

export const SegmentsMetricDropdown = ({
  selected,
  disabled,
  disabledSegments,
  onChange,
}) => {
  const options = disabledSegments
    ? segmentsOptions.map(option => ({
      ...option,
      disabled: disabledSegments.includes(option.value),
    }))
    : segmentsOptions;

  return (
    <MetricList
      selected={selected}
      disabled={disabled}
      options={options}
      onChange={onChange}
    />
  );
};

SegmentsMetricDropdown.propTypes = {
  ...basePropTypes,
  disabledSegments: T.arrayOf(T.string),
};
